/**
 * useContentUpdates Hook
 *
 * React hook for subscribing to real-time content updates via WebSocket.
 * Automatically connects on mount and disconnects on unmount.
 *
 * @example
 * // Listen for page updates
 * useContentUpdates({
 *   onPageUpdated: (slug, title) => {
 *     console.log(`Page ${slug} updated`);
 *     refetchPage(slug);
 *   }
 * });
 *
 * @example
 * // Listen for all update types
 * useContentUpdates({
 *   onPageUpdated: handlePageUpdate,
 *   onPageDeleted: handlePageDelete,
 *   onProductUpdated: handleProductUpdate,
 *   onProductDeleted: handleProductDelete,
 *   onCategoryUpdated: handleCategoryUpdate,
 *   onCategoryDeleted: handleCategoryDelete,
 *   onFAQUpdated: handleFAQUpdate,
 *   onFAQDeleted: handleFAQDelete,
 *   onReviewApproved: handleReviewApproved,
 *   onReviewRejected: handleReviewRejected,
 *   onReviewUnpublished: handleReviewUnpublished,
 *   onHeroSlideUpdated: handleHeroSlideUpdate,
 *   onHeroSlideDeleted: handleHeroSlideDelete,
 *   onHeroSlideCreated: handleHeroSlideCreate,
 *   onHeroSlideReordered: handleHeroSlideReorder,
 *   onConnected: () => console.log('Connected'),
 *   onError: (error) => console.error('Error:', error)
 * });
 */

import { useEffect, useCallback, useRef } from 'react';
import websocketService from '../services/websocketService';

/**
 * React hook for subscribing to WebSocket content updates
 *
 * @param {Object} options - Handler callbacks for different message types
 * @param {Function} options.onPageUpdated - (slug, title) => void
 * @param {Function} options.onPageDeleted - (slug) => void
 * @param {Function} options.onProductUpdated - (productId, sku, name) => void
 * @param {Function} options.onProductDeleted - (productId) => void
 * @param {Function} options.onCategoryUpdated - (categoryId, slug, name) => void
 * @param {Function} options.onCategoryDeleted - (categoryId) => void
 * @param {Function} options.onFAQUpdated - (sectionSlug, sectionTitle) => void
 * @param {Function} options.onFAQDeleted - (sectionSlug) => void
 * @param {Function} options.onReviewApproved - (reviewId, productId) => void
 * @param {Function} options.onReviewRejected - (reviewId, productId) => void
 * @param {Function} options.onReviewUnpublished - (reviewId, productId) => void
 * @param {Function} options.onHeroSlideUpdated - (page, id) => void
 * @param {Function} options.onHeroSlideDeleted - (page, id) => void
 * @param {Function} options.onHeroSlideCreated - (page, id) => void
 * @param {Function} options.onHeroSlideReordered - (page) => void
 * @param {Function} options.onPageBackgroundUpdated - (page) => void
 * @param {Function} options.onConnected - () => void
 * @param {Function} options.onDisconnected - () => void
 * @param {Function} options.onError - (error) => void
 * @returns {Object} - { isConnected }
 */
export function useContentUpdates(options = {}) {
  const optionsRef = useRef(options);

  // Update ref when options change (allows handlers to use latest closures)
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  // Stable handler wrappers that use optionsRef
  const handlePageUpdated = useCallback((payload) => {
    optionsRef.current.onPageUpdated?.(payload.slug, payload.title);
  }, []);

  const handlePageDeleted = useCallback((payload) => {
    optionsRef.current.onPageDeleted?.(payload.slug);
  }, []);

  const handleProductUpdated = useCallback((payload) => {
    optionsRef.current.onProductUpdated?.(payload.productId, payload.sku, payload.name);
  }, []);

  const handleProductDeleted = useCallback((payload) => {
    optionsRef.current.onProductDeleted?.(payload.productId);
  }, []);

  const handleCategoryUpdated = useCallback((payload) => {
    optionsRef.current.onCategoryUpdated?.(payload.categoryId, payload.slug, payload.name);
  }, []);

  const handleCategoryDeleted = useCallback((payload) => {
    optionsRef.current.onCategoryDeleted?.(payload.categoryId);
  }, []);

  const handleFAQUpdated = useCallback((payload) => {
    optionsRef.current.onFAQUpdated?.(payload.sectionSlug, payload.sectionTitle);
  }, []);

  const handleFAQDeleted = useCallback((payload) => {
    optionsRef.current.onFAQDeleted?.(payload.sectionSlug);
  }, []);

  const handleReviewApproved = useCallback((payload) => {
    // Backend sends productId and reviewId as strings
    if (process.env.NODE_ENV === 'development') {
      console.log('[useContentUpdates] REVIEW_APPROVED received:', payload);
    }
    const productId = parseInt(payload.productId, 10);
    const reviewId = parseInt(payload.reviewId, 10);
    optionsRef.current.onReviewApproved?.(reviewId, productId);
  }, []);

  const handleReviewRejected = useCallback((payload) => {
    // Backend sends productId and reviewId as strings
    if (process.env.NODE_ENV === 'development') {
      console.log('[useContentUpdates] REVIEW_REJECTED received:', payload);
    }
    const productId = parseInt(payload.productId, 10);
    const reviewId = parseInt(payload.reviewId, 10);
    optionsRef.current.onReviewRejected?.(reviewId, productId);
  }, []);

  const handleReviewUnpublished = useCallback((payload) => {
    // Backend sends productId and reviewId as strings
    if (process.env.NODE_ENV === 'development') {
      console.log('[useContentUpdates] REVIEW_UNPUBLISHED received:', payload);
    }
    const productId = parseInt(payload.productId, 10);
    const reviewId = parseInt(payload.reviewId, 10);
    optionsRef.current.onReviewUnpublished?.(reviewId, productId);
  }, []);

  const handleHeroSlideUpdated = useCallback((payload) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[useContentUpdates] HEROSLIDE_UPDATED received:', payload);
    }
    optionsRef.current.onHeroSlideUpdated?.(payload.page, payload.id);
  }, []);

  const handleHeroSlideDeleted = useCallback((payload) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[useContentUpdates] HEROSLIDE_DELETED received:', payload);
    }
    optionsRef.current.onHeroSlideDeleted?.(payload.page, payload.id);
  }, []);

  const handleHeroSlideCreated = useCallback((payload) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[useContentUpdates] HEROSLIDE_CREATED received:', payload);
    }
    optionsRef.current.onHeroSlideCreated?.(payload.page, payload.id);
  }, []);

  const handleHeroSlideReordered = useCallback((payload) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[useContentUpdates] HEROSLIDE_REORDERED received:', payload);
    }
    optionsRef.current.onHeroSlideReordered?.(payload.page);
  }, []);

  const handlePageBackgroundUpdated = useCallback((payload) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[useContentUpdates] PAGEBACKGROUND_UPDATED received:', payload);
    }
    optionsRef.current.onPageBackgroundUpdated?.(payload.page);
  }, []);

  useEffect(() => {
    // Register message handlers
    websocketService.on('PAGE_UPDATED', handlePageUpdated);
    websocketService.on('PAGE_DELETED', handlePageDeleted);
    websocketService.on('PRODUCT_UPDATED', handleProductUpdated);
    websocketService.on('PRODUCT_DELETED', handleProductDeleted);
    websocketService.on('CATEGORY_UPDATED', handleCategoryUpdated);
    websocketService.on('CATEGORY_DELETED', handleCategoryDeleted);
    websocketService.on('FAQ_UPDATED', handleFAQUpdated);
    websocketService.on('FAQ_DELETED', handleFAQDeleted);
    websocketService.on('REVIEW_APPROVED', handleReviewApproved);
    websocketService.on('REVIEW_REJECTED', handleReviewRejected);
    websocketService.on('REVIEW_UNPUBLISHED', handleReviewUnpublished);
    websocketService.on('HEROSLIDE_UPDATED', handleHeroSlideUpdated);
    websocketService.on('HEROSLIDE_DELETED', handleHeroSlideDeleted);
    websocketService.on('HEROSLIDE_CREATED', handleHeroSlideCreated);
    websocketService.on('HEROSLIDE_REORDERED', handleHeroSlideReordered);
    websocketService.on('PAGEBACKGROUND_UPDATED', handlePageBackgroundUpdated);

    // Connect to WebSocket server
    websocketService.connect(
      () => {
        if (process.env.NODE_ENV === 'development') {
          console.log('[useContentUpdates] WebSocket connected');
        }
        optionsRef.current.onConnected?.();
      },
      () => {
        if (process.env.NODE_ENV === 'development') {
          console.log('[useContentUpdates] WebSocket disconnected');
        }
        optionsRef.current.onDisconnected?.();
      },
      (error) => {
        if (process.env.NODE_ENV === 'development') {
          console.error('[useContentUpdates] WebSocket error:', error);
        }
        optionsRef.current.onError?.(error);
      }
    );

    // Cleanup on unmount
    return () => {
      // Note: We don't disconnect here because the WebSocketService is a singleton
      // and may be used by other components. The service manages its own lifecycle.
      // Handlers are removed by the service when it disconnects.
      if (process.env.NODE_ENV === 'development') {
        console.log('[useContentUpdates] Component unmounted');
      }
    };
  }, [
    handlePageUpdated,
    handlePageDeleted,
    handleProductUpdated,
    handleProductDeleted,
    handleCategoryUpdated,
    handleCategoryDeleted,
    handleFAQUpdated,
    handleFAQDeleted,
    handleReviewApproved,
    handleReviewRejected,
    handleReviewUnpublished,
    handleHeroSlideUpdated,
    handleHeroSlideDeleted,
    handleHeroSlideCreated,
    handleHeroSlideReordered,
    handlePageBackgroundUpdated
  ]);

  return {
    isConnected: websocketService.isConnected()
  };
}

export default useContentUpdates;
